import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Button, CircularProgress, Typography } from '@material-ui/core';
import { Save as SaveIcon, HighlightOff as HighlightOffIcon } from '@material-ui/icons';

import { SectionWrapper } from 'components';
import PlatformCostFields, {
  getDefaultPlatformCost,
  mapPlatformCostConfig,
  normalizePlatformCost,
  isOnlyLocationsChanged,
  omitTierIds,
  buildPlatformCostPayload,
} from 'components/PlatformCostFields';
import { PLATFORM_COST_SERVICES } from 'Services';
import { PLATFORM_COST_MODE } from 'Views/Common/enum';
import Utils from 'Shared/Utils';
import { Context } from 'context';
import { ADD_NOTIFICATION } from 'context/actions';

/**
 * Group Info/edit "Platform Cost" section — owns loading the existing config,
 * dirty detection against the loaded snapshot, and its own Save/Cancel.
 */
const PlatformCost = (props) => {
  const { levelData = {}, locationList = [], onUpdate } = props;
  const { group_id } = levelData;

  const { dispatch: globalDispatch } = useContext(Context);
  const dispatchNotification = (severity, message) =>
    globalDispatch({
      type: ADD_NOTIFICATION,
      payload: { notification: { severity, message } },
    });

  const [pc, setPc] = useState(getDefaultPlatformCost());
  const [pcSnapshot, setPcSnapshot] = useState(null);
  const [pcLoading, setPcLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // ── Load platform cost config on mount ───────────────────────
  useEffect(() => {
    const loadPlatformCost = async () => {
      setPcLoading(true);
      try {
        const res = await PLATFORM_COST_SERVICES.getPlatformCostConfig(group_id);
        if (Utils.checkIfSuccess(res)) {
          const mapped = mapPlatformCostConfig(res.data);
          setPc(mapped);
          setPcSnapshot(mapped);
        } else {
          setPcSnapshot(getDefaultPlatformCost());
        }
      } catch (err) {
        // 404 is expected for groups with no PC config yet
        console.error(err || 'Error while loading the platform cost config');
        setPcSnapshot(getDefaultPlatformCost());
      } finally {
        setPcLoading(false);
      }
    };

    if (group_id) {
      loadPlatformCost();
    } else {
      setPcLoading(false);
    }
  }, [group_id]);

  // Every field except the mode dropdown is hidden while the mode is "None", so
  // switching to it reverts the rest of the config to the last saved state.
  // Without this, edits made while Flat/Tier was briefly selected (a tier
  // amount, the billing start date, …) would ride along invisibly into the
  // disable payload and be stored as the config the user never confirmed.
  const handlePcChange = (next) => {
    const switchingToNone =
      next.pcMode === PLATFORM_COST_MODE.NONE && pc.pcMode !== PLATFORM_COST_MODE.NONE;
    setPc(
      switchingToNone && pcSnapshot
        ? { ...pcSnapshot, pcMode: PLATFORM_COST_MODE.NONE }
        : next,
    );
  };

  const isDirty = pcSnapshot != null && !_.isEqual(omitTierIds(pc), omitTierIds(pcSnapshot));
  const showSaveCancel = !pcLoading && isDirty;

  const saveChanges = async () => {
    setIsSaving(true);
    try {
      const call = isOnlyLocationsChanged(pc, pcSnapshot)
        ? await PLATFORM_COST_SERVICES.updatePlatformCostLocations({
            group_id,
            location_ids: pc.pcLocationIds,
          })
        : await PLATFORM_COST_SERVICES.upsertPlatformCostConfig(
            buildPlatformCostPayload(group_id, pc),
          );

      if (!Utils.checkIfSuccess(call)) {
        throw call?.message || call?.error || 'Error updating the platform cost';
      }

      const normalized = normalizePlatformCost(pc);
      setPc(normalized);
      setPcSnapshot(normalized);

      dispatchNotification('success', 'Platform cost updated successfully');
      onUpdate?.('Settings');
    } catch (err) {
      dispatchNotification('error', err || 'Error updating the platform cost');
    } finally {
      setIsSaving(false);
    }
  };

  const cancelChanges = () => {
    if (pcSnapshot) setPc(pcSnapshot);
  };

  return (
    <SectionWrapper
      title="Platform Cost"
      rightSection={
        showSaveCancel && (
          <>
            <Button
              size="small"
              onClick={saveChanges}
              disabled={isSaving}
              data-testid="save-platform-cost-changes-button"
              startIcon={
                isSaving ? (
                  <CircularProgress size={20} color="primary" />
                ) : (
                  <SaveIcon color="secondary" />
                )
              }
            >
              <Typography color="secondary">Save Changes</Typography>
            </Button>
            <Button
              size="small"
              className="mg_left_8"
              onClick={cancelChanges}
              disabled={isSaving}
              data-testid="cancel-platform-cost-changes-button"
              startIcon={<HighlightOffIcon disabled={isSaving} className="theme_color_orange" />}
            >
              <Typography className="theme_color_orange">Cancel</Typography>
            </Button>
          </>
        )
      }
    >
      {pcLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
          <CircularProgress size={24} color="primary" data-testid="platform-cost-loading" />
        </div>
      ) : (
        <PlatformCostFields value={pc} onChange={handlePcChange} locationList={locationList} />
      )}
    </SectionWrapper>
  );
};

PlatformCost.propTypes = {
  levelData: PropTypes.object,
  locationList: PropTypes.array,
  onUpdate: PropTypes.func,
};

export default PlatformCost;
